/**
 * Shared photo orientation / aspect-ratio logic for view-mode layouts (Primary, Motif).
 *
 * Wedding-website photos store optional natural `width`/`height` (px). We derive an aspect ratio and clamp it to a
 * sane range so extreme images don't break the layout, while still preserving portrait vs landscape. Missing
 * dimensions fall back to landscape 4:3. See layout-motif/docs/bem-contract.md (Photo).
 */

// Fixed sizes coming from design
const LANDSCAPE_DESKTOP_WIDTH = 640;
const PORTRAIT_DESKTOP_WIDTH = 440;
const LANDSCAPE_MOBILE_WIDTH = 587;
const PORTRAIT_MOBILE_WIDTH = PORTRAIT_DESKTOP_WIDTH;
const MAX_RATIO = 4 / 3; // 1.3333333333
const MIN_RATIO = 3 / 4; // 0.75

export type PhotoOrientationKind = 'landscape' | 'portrait';

export interface PhotoOrientation {
  /** Whether the (clamped) photo is wider than tall. */
  orientation: PhotoOrientationKind;
  /** CSS `aspect-ratio` value, e.g. '4 / 3' or '1.2 / 1'. */
  aspectRatioString: string;
  desktopWidth: number;
  desktopHeight: number;
  mobileWidth: number;
  mobileHeight: number;
}

/**
 * Compute the orientation, clamped aspect ratio and CDN request sizes for a photo.
 * - ratio > 4/3 → clamped landscape 4:3
 * - ratio < 3/4 → clamped portrait 3:4
 * - otherwise   → original ratio preserved
 * - missing dims → landscape 4:3 fallback
 */
export const getPhotoOrientation = ({
  width,
  height,
}: {
  width?: number;
  height?: number;
}): PhotoOrientation => {
  let actualRatio = 4 / 3;
  // Only use the logic if we have valid dimensions; otherwise fall back to landscape
  if (width && height) {
    actualRatio = width / height;
  }

  if (actualRatio > MAX_RATIO) {
    // Cap at 4:3 (landscape)
    return {
      orientation: 'landscape',
      aspectRatioString: '4 / 3',
      desktopWidth: LANDSCAPE_DESKTOP_WIDTH,
      desktopHeight: LANDSCAPE_DESKTOP_WIDTH / MAX_RATIO,
      mobileWidth: LANDSCAPE_MOBILE_WIDTH,
      mobileHeight: LANDSCAPE_MOBILE_WIDTH / MAX_RATIO,
    };
  }
  if (actualRatio < MIN_RATIO) {
    // Cap at 3:4 (portrait)
    return {
      orientation: 'portrait',
      aspectRatioString: '3 / 4',
      desktopWidth: PORTRAIT_DESKTOP_WIDTH,
      desktopHeight: PORTRAIT_DESKTOP_WIDTH / MIN_RATIO,
      mobileWidth: PORTRAIT_MOBILE_WIDTH,
      mobileHeight: PORTRAIT_MOBILE_WIDTH / MIN_RATIO,
    };
  }
  // Original orientation
  return {
    orientation: actualRatio > 1 ? 'landscape' : 'portrait',
    aspectRatioString: `${actualRatio} / 1`,
    desktopWidth: actualRatio > 1 ? LANDSCAPE_DESKTOP_WIDTH : PORTRAIT_DESKTOP_WIDTH,
    desktopHeight:
      actualRatio > 1
        ? LANDSCAPE_DESKTOP_WIDTH / actualRatio
        : PORTRAIT_DESKTOP_WIDTH / actualRatio,
    mobileWidth: actualRatio > 1 ? LANDSCAPE_MOBILE_WIDTH : PORTRAIT_MOBILE_WIDTH,
    mobileHeight:
      actualRatio > 1 ? LANDSCAPE_MOBILE_WIDTH / actualRatio : PORTRAIT_MOBILE_WIDTH / actualRatio,
  };
};
